import {
  endOfDay,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

/**
 * Format date/time in a specific timezone.
 * @param {Date | string | number} date - The date to be formatted.
 * @param {string} formatStr - The format string (date-fns format).
 * @param {string} timezone - The IANA timezone (e.g., 'Asia/Jakarta').
 * @return {string} The formatted date/time in the specified timezone.
 */
export const formatInTimezone = (
  date: Date | string | number,
  formatStr: string,
  timezone?: string
): string => {
  try {
    if (date == null) return '';

    const dateObj =
      typeof date === 'string' || typeof date === 'number'
        ? new Date(date)
        : date;

    // Validate that the date is valid
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date provided to formatInTimezone:', date);
      return '';
    }

    if (!timezone) {
      return format(dateObj, formatStr);
    }

    return formatInTimeZone(dateObj, timezone, formatStr);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return '';
  }
};

/**
 * Get start of day (00:00:00) in a specific timezone.
 * Returns a Date object representing midnight in the specified timezone.
 * @param {Date} date - The date to get start of day for.
 * @param {string} timezone - The IANA timezone (e.g., 'Asia/Jakarta').
 * @return {Date} Date object representing start of day in that timezone.
 */
export const startOfDayInTimezone = (date: Date, timezone: string): Date => {
  try {
    // Validate that the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided to startOfDayInTimezone:', date);
      return new Date(NaN);
    }

    // Convert to target timezone
    const zonedDate = utcToZonedTime(date, timezone);
    // Get start of day in that timezone
    const startOfDayZoned = startOfDay(zonedDate);
    // Convert back to UTC-based Date object
    return zonedTimeToUtc(startOfDayZoned, timezone);
  } catch (error) {
    console.error('Error getting start of day in timezone:', error);
    return startOfDay(date);
  }
};

/**
 * Get end of day (23:59:59.999) in a specific timezone.
 * Returns a Date object representing end of day in the specified timezone.
 * @param {Date} date - The date to get end of day for.
 * @param {string} timezone - The IANA timezone (e.g., 'Asia/Jakarta').
 * @return {Date} Date object representing end of day in that timezone.
 */
export const endOfDayInTimezone = (date: Date, timezone: string): Date => {
  try {
    // Validate that the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided to endOfDayInTimezone:', date);
      return new Date(NaN);
    }

    // Convert to target timezone
    const zonedDate = utcToZonedTime(date, timezone);
    // Get end of day in that timezone
    const endOfDayZoned = endOfDay(zonedDate);
    // Convert back to UTC-based Date object
    return zonedTimeToUtc(endOfDayZoned, timezone);
  } catch (error) {
    console.error('Error getting end of day in timezone:', error);
    return endOfDay(date);
  }
};

/**
 * Get start of month (first day at 00:00:00) in a specific timezone.
 * Returns a Date object representing first day of month in the specified timezone.
 * @param {Date} date - The date to get start of month for.
 * @param {string} timezone - The IANA timezone (e.g., 'Asia/Jakarta').
 * @return {Date} Date object representing start of month in that timezone.
 */
export const startOfMonthInTimezone = (date: Date, timezone: string): Date => {
  try {
    // Validate that the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided to startOfMonthInTimezone:', date);
      return new Date(NaN);
    }

    // Convert to target timezone
    const zonedDate = utcToZonedTime(date, timezone);
    // Get start of month in that timezone
    const startOfMonthZoned = startOfMonth(zonedDate);
    // Convert back to UTC-based Date object
    return zonedTimeToUtc(startOfMonthZoned, timezone);
  } catch (error) {
    console.error('Error getting start of month in timezone:', error);
    return startOfMonth(date);
  }
};

/**
 * Get start of week (Monday at 00:00:00) in a specific timezone.
 * Returns a Date object representing start of week in the specified timezone.
 * @param {Date} date - The date to get start of week for.
 * @param {string} timezone - The IANA timezone (e.g., 'Asia/Jakarta').
 * @return {Date} Date object representing start of week in that timezone.
 */
export const startOfWeekInTimezone = (date: Date, timezone: string): Date => {
  try {
    // Validate that the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided to startOfWeekInTimezone:', date);
      return new Date(NaN);
    }

    // Convert to target timezone
    const zonedDate = utcToZonedTime(date, timezone);
    // Get start of week in that timezone (Sunday as week start)
    const startOfWeekZoned = startOfWeek(zonedDate, { weekStartsOn: 0 });
    // Convert back to UTC-based Date object
    return zonedTimeToUtc(startOfWeekZoned, timezone);
  } catch (error) {
    console.error('Error getting start of week in timezone:', error);
    return startOfWeek(date, { weekStartsOn: 0 });
  }
};

/**
 * Get end of week (Sunday at 23:59:59.999) in a specific timezone.
 * Returns a Date object representing end of week in the specified timezone.
 * @param {Date} date - The date to get end of week for.
 * @param {string} timezone - The IANA timezone (e.g., 'Asia/Jakarta').
 * @return {Date} Date object representing end of week in that timezone.
 */
export const endOfWeekInTimezone = (date: Date, timezone: string): Date => {
  try {
    // Validate that the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided to endOfWeekInTimezone:', date);
      return new Date(NaN);
    }

    // Convert to target timezone
    const zonedDate = utcToZonedTime(date, timezone);
    // Get end of week in that timezone (Saturday as week end)
    const endOfWeekZoned = endOfWeek(zonedDate, { weekStartsOn: 0 });
    // Convert back to UTC-based Date object
    return zonedTimeToUtc(endOfWeekZoned, timezone);
  } catch (error) {
    console.error('Error getting end of week in timezone:', error);
    return endOfWeek(date, { weekStartsOn: 0 });
  }
};

/** Returns the hours and minutes between two times, as in `2h 05m`. Returns `0m` when `since` is not before `now`. */
export const timeSince = (since: string | Date, now: number): string => {
  const minutes = Math.max(
    0,
    Math.floor((now - new Date(since).getTime()) / 60_000)
  );
  const hours = Math.floor(minutes / 60);
  return hours > 0
    ? `${hours}h ${String(minutes % 60).padStart(2, '0')}m`
    : `${minutes}m`;
};
